import { useLingui } from "@lingui/react"
import { IconPlayerPlayFilled } from "@tabler/icons-react"
import { Link } from "@tanstack/react-router"
import { formatClock, formatShortDate } from "../../lib/format-date"
import { displayMeetingTitle } from "../../lib/meeting-title"
import { speakerVMFromName } from "../../lib/speaker-vm"
import { ChannelLabel, channelMeta } from "../channel-meta"
import { SpeakerChip } from "../SpeakerChip"
import type { IProps } from "./interfaces"

import classes from "./styles.module.css"

/**
 * One grounding source under an assistant answer — the evidence-first card that
 * replaced the citation chip. The whole card is a router `<Link>` to its source
 * meeting, deep-linking to the exact moment (`?t=`) when the citation carries a
 * seek position; no-seek citations link to the meeting without `t`.
 *
 * The source channel drives a semantic `data-tone` accent + icon (via
 * `channel-meta`) — never a literal palette name (theming rule #6). The speaker,
 * when present, renders as a coloured {@link SpeakerChip} (identity hue routed
 * through `speakerTextColor` for WCAG-safe contrast); with no speaker the channel
 * label carries the context alone. Deliberately no left accent rail — tone rides
 * the channel pill (an AI-design-tell Gerard vetoed for callout/emphasis blocks).
 * Pure presentation.
 */
export const EvidenceCard = ({ citation }: IProps): React.JSX.Element => {
  const { i18n } = useLingui()
  const { tone, Icon } = channelMeta(citation.sourceChannel)
  const seekStart = citation.seekStart
  const canSeek = seekStart != null
  const date = formatShortDate(i18n, citation.meetingDate)
  const title = displayMeetingTitle(citation.meetingSubject, null, i18n)
  const quote = citation.quote?.trim()
  // Synthesise a render-ready VM from the name so the chip gets an identity hue +
  // initials via the standard derivation (no meeting context needed here).
  const speakerVM = citation.speaker ? speakerVMFromName(citation.speaker) : null

  return (
    <Link
      to="/meetings/$id"
      params={{ id: citation.meetingId }}
      search={canSeek ? { t: Math.floor(seekStart) } : {}}
      className={`${classes.card} lex-surface-card`}
      data-tone={tone}
      data-testid="evidence-card"
      title={`${title} · ${date}`}
    >
      <div className={classes.head}>
        <span className={classes.channel}>
          <Icon size={13} />
          <ChannelLabel sourceChannel={citation.sourceChannel} />
        </span>
        {canSeek ? (
          <span className={classes.seek}>
            <IconPlayerPlayFilled size={10} aria-hidden />
            <span className={classes.seekTime}>{formatClock(seekStart)}</span>
          </span>
        ) : null}
      </div>

      {speakerVM ? (
        <span className={classes.speaker}>
          <SpeakerChip speaker={speakerVM} variant="inline" size="sm" />
        </span>
      ) : null}

      {quote ? <p className={classes.quote}>{quote}</p> : null}

      <span className={classes.meta}>
        <span className={classes.subject}>{title}</span>
        <span className={`${classes.dot} lex-dot`} aria-hidden />
        <time className={classes.date}>{date}</time>
      </span>
    </Link>
  )
}
